/**
 * Authenticated app layout — top bar + responsive sidebar + content area.
 *
 * Mounted at `/app/*`. Children routes render inside `<Outlet />`.
 *
 * Responsive behavior:
 *   - <md  (mobile)  : sidebar hidden; top-bar hamburger opens a drawer.
 *   - md..lg (tablet): collapsed icon rail (16ch wide); labels in tooltip.
 *   - lg+   (desktop): full sidebar with labels.
 *
 * Phase 0: nav surfaces Tier 1 (Devices, Dashboards, Alarms), Tier 2
 * (lazy-loaded; only renders when `pack:foundry` is enabled), and Platform
 * (Admin). Phase 1 wires up Zitadel session + OpenFGA-derived nav visibility.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Building2,
  Check,
  ChevronsUpDown,
  GraduationCap,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Settings,
  Sparkles,
  Sun,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { isLiveApi, orgsApi } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { resetOnboardingState } from "./onboarding";
import { useTenantTheme, useTenantThemeAdmin } from "@/lib/tenant-theme";
import { cn } from "@/lib/ui/cn";
import { CoBrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/lib/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/lib/ui/tooltip";
import { PageShellContext } from "@/lib/ui/page-shell-context";
import { useAppMenu } from "@/lib/nav/use-app-menu";
import { keepPageOnSwitch } from "@/lib/nav/landing";
import { DesktopMenu, MobileMenu } from "@/components/nav/AppMenu";
import { CopilotLauncher } from "@/components/copilot/CopilotLauncher";
import { CopilotPanel } from "@/components/copilot/CopilotPanel";
import { TourProvider, useTour } from "@/lib/tutorials/tour-controller";
import { TourOverlay } from "@/lib/tutorials/TourOverlay";
import { AppFooter } from "@/components/nav/AppFooter";
import { AppSwitcher } from "@/components/nav/AppSwitcher";
import { dismissNudge, hasCompletedAny, isNudgeDismissed } from "@/lib/tutorials/progress";
import { useMyUnackedAlertsQuery } from "@/lib/api/station-alerts";
import { alarmsLabel } from "@/lib/plant/station-alerts";

export function AppLayout() {
  const { user, logout } = useAuth();
  const myUnackedAlerts = useMyUnackedAlertsQuery().data ?? 0;
  const tenant = useTenantTheme();
  const { activeOrg } = useOrg();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // The menu (lib/nav/menu.ts): three areas, each item hidden where the org or the viewer does not have it.
  const { areas } = useAppMenu();

  const orgName = activeOrg?.orgName ?? "—";
  const initials = (user?.name ?? "?")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <PageShellContext.Provider
      value={{
        isMobileNavOpen: mobileNavOpen,
        openMobileNav: () => setMobileNavOpen(true),
        closeMobileNav: () => setMobileNavOpen(false),
      }}
    >
      {/* Tour controller scope (#124) — wraps the whole authed shell so the
          Tutorials hub (in <Outlet/>) can start a tour and the global
          <TourOverlay/> below can drive it over the real pages. */}
      <TourProvider>
      <TooltipProvider delayDuration={250}>
        <div
          data-app-shell
          className="flex h-screen flex-col bg-surface text-on-surface"
        >
          {/* Top bar — spans full width */}
          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface-elevated px-3 md:px-5">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="size-5" />
            </Button>

            <Link to="/app" className="flex items-center gap-2">
              <CoBrandMark />
              {/* The product's name beside the logo, always (PRODUCT_NAME); the org's is the switcher's. */}
              <span className="hidden text-sm font-semibold text-on-surface md:inline">{tenant.productName}</span>
            </Link>

            {/* Org switcher — active org + switch + create-new. */}
            <OrgSwitcher />

            <div className="ml-auto flex items-center gap-2">
              {/* The search box that searched nothing, and its Cmd+K badge, are gone (Navigation review, D9). */}
              <ThemeModeButton />

              {/* The suite's other products for this org (lib/suite); hidden when there is none. */}
              <AppSwitcher productName={tenant.productName} />

              <CopilotLauncher />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild variant="ghost" size="icon" aria-label={alarmsLabel(myUnackedAlerts)}>
                    <Link to="/app/alarms" className="relative">
                      <Bell className="size-5" />
                      {/* The open alerts at the person's stations (station workspaces, step 7). */}
                      {myUnackedAlerts > 0 && (
                        <span
                          aria-hidden
                          className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] font-semibold leading-4 text-danger-foreground"
                        >
                          {myUnackedAlerts > 99 ? "99+" : myUnackedAlerts}
                        </span>
                      )}
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{alarmsLabel(myUnackedAlerts)}</TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Account menu"
                    className="flex items-center gap-2 rounded-full p-0.5 outline-none ring-offset-1 transition-colors hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Avatar>
                      {user?.avatarUrl && (
                        <AvatarImage src={user.avatarUrl} alt={user.name} />
                      )}
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-on-surface">
                        {user?.name}
                      </span>
                      <span className="text-xs text-on-surface-muted">
                        {user?.email}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/app/settings">
                      <Settings className="mr-2 size-4" /> Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => logout()}>
                    <LogOut className="mr-2 size-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Body — sidebar + content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Desktop sidebar */}
            <DesktopMenu areas={areas} person={user?.id} />

            {/* Mobile drawer */}
            <MobileMenu
              open={mobileNavOpen}
              onClose={() => setMobileNavOpen(false)}
              areas={areas}
              productName={tenant.productName}
              orgName={orgName}
              person={user?.id}
            />

            {/* The page's one scroll. Relative, so whatever a page places absolutely (a screen reader's sr-only text)
                is placed in it and scrolls with it: unpositioned, such an element was placed against the window,
                outside this scroll, and stretched the document into a second scrollbar over an empty canvas
                (Arjun's notes after the 2026-10-06 deploy). The footer ends the scroll; on a short page the page's
                column grows to put it at the window's foot. */}
            <main
              data-pattern-canvas
              className="relative flex flex-1 flex-col overflow-auto bg-surface"
            >
              <div className="flex-1">
                <Outlet />
              </div>
              <AppFooter productName={tenant.productName} />
            </main>

            {/* Long-lived right-side Copilot panel (toggled via top-bar
                CopilotLauncher or Cmd/Ctrl+J). Different z-index +
                anchor strategy from the Sheet primitive so single-shot
                Sheets and the Copilot panel never fight. */}
            <CopilotPanel />
          </div>

          {/* Guided-tutorial overlay (#124) — mounted ONCE here, inside the
              router + providers. Inert until a tour is started from the
              Tutorials hub; then it navigates to each step's real route and
              spotlights the real element. Renders nothing while idle. */}
          <TourOverlay />

          {/* First-login nudge (#122 follow-up) — a small, dismissible "take
              the tour" card shown only to brand-new users (zero tutorials
              completed) who haven't dismissed it. One-shot, localStorage-gated.
              Sits beside the overlay so it's inside the tour-controller scope. */}
          <FirstLoginTourNudge />
        </div>
      </TooltipProvider>
      </TourProvider>
    </PageShellContext.Provider>
  );
}

/**
 * First-login tour nudge (#122 follow-up) — a small, dismissible card inviting
 * brand-new users to take the 2-minute "Create a Thing" tour.
 *
 * Visibility rules (localStorage-gated, shows at most once per browser profile):
 *   - the user has completed ZERO tutorials, AND
 *   - they haven't dismissed this nudge before, AND
 *   - no tour is currently running (so it never overlaps the tour UI).
 *
 * Both "Take the tour" and the dismiss control persist the dismissal, so the
 * nudge won't reappear. We reference the tour by id string ("create-a-thing")
 * rather than importing the definitions module. Mounted inside <TourProvider>
 * (see AppLayout) so it can drive the controller.
 */
function FirstLoginTourNudge() {
  const { start, isActive } = useTour();
  // Decide once on mount, client-side only — localStorage isn't available
  // during SSR, and we don't want the card to flash before the gate resolves.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!hasCompletedAny() && !isNudgeDismissed()) {
      setShow(true);
    }
  }, []);

  if (!show || isActive) return null;

  const dismiss = () => {
    dismissNudge();
    setShow(false);
  };

  const takeTour = () => {
    dismissNudge();
    setShow(false);
    start("create-a-thing");
  };

  return (
    <div
      role="dialog"
      aria-label="Take a guided tour"
      // Never on paper: it floats over whatever page is printed (a brief's print view, D5).
      data-no-print=""
      className="fixed bottom-4 right-4 z-40 flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-4 text-on-surface shadow-xl animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
          <GraduationCap className="size-5" />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-semibold leading-tight text-on-surface">
            New here?
          </p>
          <p className="text-xs leading-relaxed text-on-surface-muted">
            Take the 2-minute tour — we'll walk you through adding your first
            data source on the real pages.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-1 -mt-1 size-7 shrink-0 text-on-surface-muted"
          aria-label="Dismiss tour invitation"
          onClick={dismiss}
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Not now
        </Button>
        <Button variant="primary" size="sm" onClick={takeTour}>
          <Sparkles className="mr-1.5 size-3.5" />
          Take the tour
        </Button>
      </div>
    </div>
  );
}

/**
 * Org switcher (multi-tenancy Phase F) — shows the active org and lets the
 * user switch between their memberships or create a new org. Switching does a
 * full reload so every request (and the not-org-keyed React Query caches +
 * plant/dashboard stores) re-resolves under the new X-Org-Id.
 */
/**
 * "SAMPLE DATA" — the marker that tells a reader an organisation's numbers are
 * not measurements.
 *
 * A sandbox renders identically to a real plant: same charts, same defect
 * counts, same scrap rate. That is the point of a sandbox and it is also the
 * hazard — a screenshot of one, or a glance at the wrong tab, is indistinguishable
 * from the real thing. So the marker rides the org switcher, which is present on
 * every page, rather than living only on the Admin screen.
 *
 * Absent (not "REAL", not a skeleton) on a real plant and while the mode is
 * still loading: a real plant is the unremarkable case and needs no decoration,
 * and flashing a badge that later disappears is worse than showing it late.
 */
function SandboxBadge({ orgId, className }: { orgId?: string; className?: string }) {
  const { data } = useQuery({
    queryKey: ["org-data-mode", orgId],
    queryFn: () => orgsApi.get(orgId!),
    enabled: Boolean(orgId) && isLiveApi,
    staleTime: 5 * 60_000, // the mode changes at most once, one way
  });
  if (data?.data_mode !== "demo") return null;
  return (
    <span
      title="Every number in this organisation is sample data, not a measurement."
      className={cn(
        "shrink-0 rounded-full bg-warning/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning",
        className,
      )}
    >
      Sample
    </span>
  );
}

function OrgSwitcher() {
  const { orgs, activeOrg, setActiveOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();

  const switchOrg = (orgId: string) => {
    if (orgId === activeOrg?.orgId) return;
    // A menu page, with its tab, is kept where the new org shows it (lib/nav/landing.ts, D11); the landing decides.
    keepPageOnSwitch(location.pathname, location.search);
    setActiveOrg(orgId); // persists to localStorage + sets X-Org-Id
    window.location.assign("/app"); // hard reload → all data reloads for the new org
  };

  const createOrg = () => {
    resetOnboardingState();
    navigate("/onboarding");
  };

  // Org-less users are already being routed to /onboarding by RequireOrg.
  if (orgs.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch organisation"
          className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-on-surface outline-none transition-colors hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Building2 className="size-3.5 text-on-surface-muted" />
          <span className="hidden max-w-[10rem] truncate sm:inline">
            {activeOrg?.orgName ?? "Select org"}
          </span>
          {/*
            The sandbox marker. It lives on the switcher because that control is
            on EVERY page — a sandbox's numbers look exactly like a real plant's,
            and without a persistent marker the only place that distinction was
            visible was the Admin page. Someone reading a scrap rate needs to
            know which kind of organisation they are reading it from.
          */}
          <SandboxBadge orgId={activeOrg?.orgId} />
          <ChevronsUpDown className="size-3 text-on-surface-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel>Organisations</DropdownMenuLabel>
        {orgs.map((o) => (
          <DropdownMenuItem key={o.orgId} onSelect={() => switchOrg(o.orgId)}>
            <Check
              className={cn(
                "mr-2 size-4 shrink-0",
                o.orgId === activeOrg?.orgId
                  ? "text-brand opacity-100"
                  : "opacity-0",
              )}
            />
            <span className="truncate">{o.orgName}</span>
            <SandboxBadge orgId={o.orgId} className="ml-auto" />
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={createOrg}>
          <Plus className="mr-2 size-4" /> Create organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Re-export for downstream routes that need to add their own header actions
export function PageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-8", className)}>
      {children}
    </div>
  );
}

/**
 * Top-bar theme mode toggle — cycles Light → Dark → System → Light…
 * Tooltip surfaces the next mode so users know what one more click does.
 * The full palette / typography / preset editor lives in /app/admin → Branding.
 */
function ThemeModeButton() {
  const tenant = useTenantTheme();
  const admin = useTenantThemeAdmin();
  const next = tenant.mode === "light" ? "dark" : tenant.mode === "dark" ? "system" : "light";
  const Icon = tenant.mode === "light" ? Sun : tenant.mode === "dark" ? Moon : Monitor;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Theme: ${tenant.mode}. Switch to ${next}.`}
          onClick={() => admin.setMode(next)}
        >
          <Icon className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        Theme: {tenant.mode} → click for {next}
      </TooltipContent>
    </Tooltip>
  );
}
